// src/components/TutorPanel.jsx — fixed-position docked tutor sidebar.
//
// Mounts at app-shell level (above <Routes> in App.js) so the
// conversation persists across every navigation. Per M0 of the
// agentic-stack migration plan: chat is layout, not a route.
//
// Layout: 400px wide when open, 36px tab when closed. Slides in from
// the right via translateX. Main content gets `pr-[400px]` shift via
// the parent layout — kept out from under the panel when open.
//
// Inside the panel:
//   - Header: "Tutor" title + close button + proposal/action overflow
//   - Thread (assistant-ui primitives, markdown enabled)
//   - Inline action card: when `session.pending_action` is set, render
//     a button that, on click, navigates the main viewport to the
//     matching route via React Router. The panel + thread stay in
//     place. This is the core of "tutor follows you across surfaces".
//   - Inline proposal card: legacy "Create this course" CTA
//   - Composer at the bottom
//
// When closed: only a small edge tab is visible. Click → openPanel().
// Cmd/Ctrl + . toggles from anywhere (handled in TutorRuntimeContext).
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { MessageSquare, RotateCcw, X } from 'lucide-react';

import Thread from './chat/Thread';
import Opener from './chat/Opener';
import useOpenerData from './chat/useOpenerData';
import { StreakPill } from './ui/mastery';
import { DraftConceptContentUI } from './chat/tools/DraftConceptContentUI';
import { useTutor } from '../contexts/TutorRuntimeContext';
import { apiService } from '../services/api';
import { Button } from './ui';
import { useConfirm } from './ui/ConfirmDialog';

const PANEL_WIDTH = 400;

// Routes that mount their own contextual chat and hide the global tutor (the concept editor's Refine panel).
// Exported so the app shell does not reserve the panel's width where the panel never renders.
const TUTOR_SUPPRESSED = /^\/course\/[^/]+\/manage\/[^/]+\/[^/]+/;
export function isTutorSuppressed(pathname) {
  return TUTOR_SUPPRESSED.test(pathname);
}

// Action kind → frontend route. Mirror of
// `backend/app/agents/tutor/tools.py::ProposeActionInput.kind`. Adding
// a kind here means the agent can navigate the user to that surface;
// adding it on the backend without a route map here means the click
// silently no-ops.
function actionToRoute(action) {
  if (!action?.course_code) return null;
  const code = encodeURIComponent(action.course_code);
  switch (action.kind) {
    case 'open_course':
      return `/course/${code}`;
    case 'open_concept':
      if (!action.domain || !action.concept) return null;
      return `/course/${code}/manage/${encodeURIComponent(action.domain)}/${encodeURIComponent(action.concept)}`;
    case 'start_quiz':
      if (!action.domain) return null;
      return `/course/${code}/quiz/${encodeURIComponent(action.domain)}`;
    case 'open_practice':
      return `/course/${code}/practice`;
    case 'start_repair':
      if (!action.domain) return null;
      return `/course/${code}/workbench/${encodeURIComponent(action.domain)}`;
    default:
      return null;
  }
}

const ACTION_GLYPH = {
  open_practice: '▶', open_dashboard: '▤', open_mastery: '◴',
  open_course: '↗', open_quiz: '▶', start_repair: '⚒',
};

function PendingActionCard({ action, onConfirm }) {
  const route = actionToRoute(action);
  if (!route) return null;
  return (
    <div className="border-t border-line px-4 pt-3 pb-3">
      <div className="tcard is-arriving" role="group" aria-label="suggested action">
        <div className="tcard-head"><span className="tc-kicker">Where to next</span></div>
        <div className="action-row" style={{ borderTop: 0 }}>
          <button type="button" className="action-btn" onClick={() => onConfirm(route)}>
            <span className="ab-glyph">{ACTION_GLYPH[action.kind] || '→'}</span>
            <span>
              {action.label}
              {action.concept && <span style={{ color: 'var(--muted)' }}> · {action.concept}</span>}
            </span>
            <span className="arrow">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function AddConceptSummary({ payload }) {
  return (
    <p className="text-sm text-read">
      Add concept <strong>{payload?.concept}</strong> to domain{' '}
      <code className="text-xs">{payload?.domain}</code>.
    </p>
  );
}

function AddDomainSummary({ payload }) {
  const conceptCount = Array.isArray(payload?.concepts) ? payload.concepts.length : 0;
  const weightPct = payload?.weight ? Math.round(payload.weight * 100) : 0;
  return (
    <div className="text-sm text-read space-y-1">
      <p>
        Add domain <strong>{payload?.name}</strong>{' '}
        (<code className="text-xs">{payload?.key}</code>) — weight{' '}
        {weightPct}%, {conceptCount} concept{conceptCount === 1 ? '' : 's'}.
      </p>
      {Array.isArray(payload?.concepts) && payload.concepts.length > 0 && (
        <p className="text-xs text-mutedink">
          {payload.concepts.slice(0, 5).join(', ')}
          {payload.concepts.length > 5 ? '…' : ''}
        </p>
      )}
    </div>
  );
}

function RebalanceSummary({ payload }) {
  const ws = Array.isArray(payload?.weights) ? payload.weights : [];
  return (
    <div className="text-sm text-read space-y-1">
      <p>Rebalance domain weights:</p>
      <ul className="text-xs text-mutedink list-disc pl-4">
        {ws.map((w) => (
          <li key={w.key}>
            <code>{w.key}</code> → {Math.round((w.weight || 0) * 100)}%
          </li>
        ))}
      </ul>
    </div>
  );
}

function EditMetadataSummary({ payload }) {
  return (
    <div className="text-sm text-read space-y-1">
      {payload?.name && <p>Rename to <strong>{payload.name}</strong>.</p>}
      {payload?.description && (
        <p className="text-xs text-mutedink italic">"{payload.description}"</p>
      )}
    </div>
  );
}

function RenameConceptSummary({ payload }) {
  return (
    <div className="text-sm text-read space-y-1">
      <p>
        Rename concept <strong>{payload?.old_concept}</strong> →{' '}
        <strong>{payload?.new_concept}</strong> in domain{' '}
        <code className="text-xs">{payload?.domain}</code>.
      </p>
      <p className="text-xs text-mutedink">
        Updates your mastery, saved notes, and quiz history for this concept.
      </p>
    </div>
  );
}

function RemoveConceptSummary({ payload }) {
  return (
    <div className="text-sm text-read space-y-1">
      <p>
        Remove concept <strong>{payload?.concept}</strong> from domain{' '}
        <code className="text-xs">{payload?.domain}</code>.
      </p>
      <p className="text-xs text-status-warn">
        Deletes your mastery and saved notes for this concept. Quiz history is preserved.
      </p>
    </div>
  );
}

const EDIT_SUMMARY_BY_KIND = {
  add_concept: AddConceptSummary,
  add_domain: AddDomainSummary,
  rebalance: RebalanceSummary,
  edit_metadata: EditMetadataSummary,
  rename_concept: RenameConceptSummary,
  remove_concept: RemoveConceptSummary,
};

function EditSummary({ kind, payload }) {
  // Plain-text summary of the proposed change. Each kind has a
  // dedicated component so the user can see what's about to happen
  // without reading JSON.
  const Component = EDIT_SUMMARY_BY_KIND[kind];
  if (Component) return <Component payload={payload} />;
  return <p className="text-sm text-read">Curriculum edit ({kind})</p>;
}


function EditProposalCard({ edit, onApply, onDiscard, applying, error }) {
  return (
    <div className="border-t border-line px-4 pt-3 pb-3">
      <div className="tcard is-arriving" role="group" aria-label="curriculum edit">
        <div className="tcard-head">
          <span className="tc-kicker">Curriculum edit</span>
          <span className="tc-pill">draft</span>
        </div>
        <div className="tcard-body">
          <div className="pc-kicker">
            {edit.course_code}
            {edit.payload?.domain ? ` · ${edit.payload.domain}` : ''}
          </div>
          <EditSummary kind={edit.kind} payload={edit.payload} />
          {edit.rationale && <div className="pc-why">{edit.rationale}</div>}
          {error && <p className="text-xs text-status-bad mt-2">{error}</p>}
        </div>
        <div className="pc-actions">
          <button type="button" className="fn-btn fn-btn--primary" onClick={onApply} disabled={applying}>
            {applying ? 'Applying…' : 'Apply edit'}
          </button>
          <button type="button" className="fn-btn fn-btn--ghost" onClick={onDiscard} disabled={applying}>
            Discard
          </button>
        </div>
      </div>
    </div>
  );
}


function ProposalCard({ proposal, onAdd, onDiscard, building, buildStage, error }) {
  // Field Notes COURSE PROPOSAL card — same flow TutorChat.js uses
  // (generateCourseStructure → createCourse → navigate), now wired
  // into the panel so "Add to my courses" actually works here.
  return (
    <div className="border-t border-line px-4 pt-0 pb-3">
      <div className="tcard is-arriving" role="group" aria-label="course proposal">
        <div className="tcard-head">
          <span className="tc-kicker">Course proposal</span>
          <span className="tc-pill">draft</span>
        </div>
        <div className="tcard-body">
          <div className="pc-kicker">new course</div>
          <div className="pc-title">{proposal.name}</div>
          <div className="pc-desc">{proposal.description}</div>
          {proposal.why_this && <div className="pc-why">{proposal.why_this}</div>}
          {error && <p className="text-xs text-status-bad mt-2">{error}</p>}
        </div>
        {building ? (
          <div className="dc-resolved">{buildStage || 'building…'}</div>
        ) : (
          <div className="pc-actions">
            <button type="button" className="fn-btn fn-btn--primary" onClick={onAdd}>
              Add to my courses
            </button>
            <button type="button" className="fn-btn fn-btn--ghost" onClick={onDiscard}>
              Discard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function renderBelowMessages({
  pendingCurriculumEdit, proposal, pendingAction,
  handleApplyEdit, handleDiscardEdit, editApplying, editError,
  handleNavigate,
  handleAddCourse, handleDiscardProposal,
  courseBuilding, courseBuildStage, courseBuildError,
}) {
  if (pendingCurriculumEdit) {
    return (
      <EditProposalCard
        key={`${pendingCurriculumEdit.course_code}-${pendingCurriculumEdit.kind}`}
        edit={pendingCurriculumEdit}
        onApply={handleApplyEdit}
        onDiscard={handleDiscardEdit}
        applying={editApplying}
        error={editError}
      />
    );
  }
  if (proposal) {
    return (
      <ProposalCard
        proposal={proposal}
        onAdd={handleAddCourse}
        onDiscard={handleDiscardProposal}
        building={courseBuilding}
        buildStage={courseBuildStage}
        error={courseBuildError}
      />
    );
  }
  if (pendingAction) {
    return (
      <PendingActionCard
        key={`${pendingAction.kind}-${pendingAction.label}`}
        action={pendingAction}
        onConfirm={handleNavigate}
      />
    );
  }
  return null;
}


export default function TutorPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const openerData = useOpenerData();
  const {
    runtime, sessionId, proposal, pendingAction, pendingCurriculumEdit,
    resetCapture,
    error, booting, panelOpen, openPanel, closePanel,
  } = useTutor();
  const [editApplying, setEditApplying] = React.useState(false);
  const [editError, setEditError] = React.useState(null);

  const handleApplyEdit = async () => {
    if (!pendingCurriculumEdit || editApplying) return;
    setEditApplying(true);
    setEditError(null);
    try {
      await apiService.applyCurriculumEdit(pendingCurriculumEdit.course_code, {
        kind: pendingCurriculumEdit.kind,
        payload: pendingCurriculumEdit.payload,
      });
      // Clear locally + on the sidecar so the card doesn't re-hydrate
      // on the next /tutor/start.
      if (resetCapture) resetCapture();
      if (sessionId) {
        apiService.clearTutorCapture(sessionId).catch(() => {});
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to apply edit';
      setEditError(typeof msg === 'string' ? msg : 'Failed to apply edit');
    } finally {
      setEditApplying(false);
    }
  };

  const handleDiscardEdit = () => {
    if (editApplying) return;
    if (resetCapture) resetCapture();
    if (sessionId) {
      apiService.clearTutorCapture(sessionId).catch(() => {});
    }
    setEditError(null);
  };

  // Proposal → real course. Same flow as TutorChat.handleCreate:
  // generateCourseStructure(prompt from the proposal) → createCourse →
  // navigate. Stages surface as mono text in the card footer.
  const [courseBuilding, setCourseBuilding] = React.useState(false);
  const [courseBuildStage, setCourseBuildStage] = React.useState('');
  const [courseBuildError, setCourseBuildError] = React.useState(null);

  const handleAddCourse = async () => {
    if (!proposal || courseBuilding) return;
    setCourseBuilding(true);
    setCourseBuildError(null);
    try {
      setCourseBuildStage('designing the curriculum…');
      const prompt = [proposal.name, proposal.description, proposal.why_this]
        .filter(Boolean).join('. ');
      const genRes = await apiService.generateCourseStructure(prompt);
      setCourseBuildStage('saving the course…');
      const createRes = await apiService.createCourse(genRes.data.course_data);
      const created = createRes.data;
      if (resetCapture) resetCapture();
      if (sessionId) apiService.clearTutorCapture(sessionId).catch(() => {});
      navigate(`/course/${created.code || created.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to create the course';
      setCourseBuildError(typeof msg === 'string' ? msg : 'Failed to create the course');
    } finally {
      setCourseBuilding(false);
      setCourseBuildStage('');
    }
  };

  const handleDiscardProposal = () => {
    if (courseBuilding) return;
    if (resetCapture) resetCapture();
    if (sessionId) apiService.clearTutorCapture(sessionId).catch(() => {});
    setCourseBuildError(null);
  };

  const handleNavigate = (route) => {
    // The panel stays open; only the main viewport changes. Clear
    // the captured pending_action so the button doesn't linger after
    // the user has taken its action — both client-side (resetCapture)
    // and server-side (clearTutorCapture). Without the server call,
    // the button persists across refresh because /tutor/start
    // re-hydrates the panel from the still-set sidecar.
    if (resetCapture) resetCapture();
    if (sessionId) {
      apiService.clearTutorCapture(sessionId).catch(() => {
        // Best-effort; the user is mid-navigation and we don't want
        // to block on this. Worst case the button reappears on
        // refresh until the next user message wipes it.
      });
    }
    navigate(route);
  };

  const confirm = useConfirm();
  const handleStartFresh = async () => {
    // Confirm — this discards the current conversation. Cheap UX
    // brake; without it accidental clicks would lose context.
    const ok = await confirm({
      title: 'Start a fresh conversation?',
      body: 'The current thread will be archived — you can always start again from where your mastery left off.',
      confirmLabel: 'Start fresh',
    });
    if (!ok) return;
    try {
      await apiService.endActiveTutor();
    } catch {
      // Best-effort: even if end-active failed, reload — the runtime
      // re-boots and (if the prior session is somehow still active)
      // resumes; if not, creates a new one.
    }
    // Hard reload — re-boots TutorRuntimeProvider, which calls
    // startTutor again. Lighter alternative is a state-level reset
    // hook on the provider; M3 (react-langgraph) gets that for free.
    window.location.reload();
  };

  // ONE chat per screen: the concept editor mounts its own contextual
  // RefineChatPanel — stacking the global tutor opener next to it reads
  // as two competing chats (CTO report, 2026-06-10). Suppress the tutor
  // panel entirely on that route; it returns on navigation.
  if (isTutorSuppressed(location.pathname)) {
    return null;
  }

  if (!panelOpen) {
    // Collapsed state: small fixed-position tab on the right edge.
    return (
      <button
        type="button"
        onClick={openPanel}
        aria-label="Open tutor"
        className="fixed top-1/2 right-0 -translate-y-1/2 z-30 w-9 h-12 flex items-center justify-center rounded-l-lg bg-accent text-[var(--on-accent)] shadow-pop hover:bg-accent-hover transition-colors"
      >
        <MessageSquare size={18} />
      </button>
    );
  }

  return (
    <aside
      aria-label="Tutor"
      className="fixed top-0 right-0 bottom-0 z-30 bg-surface border-l border-line shadow-pop flex flex-col"
      style={{ width: `${PANEL_WIDTH}px` }}
    >
      <header className="flex items-center gap-3 border-b border-line px-4 py-3">
        <span className="ph-avatar" aria-hidden="true">A</span>
        <div>
          <p className="font-heading font-semibold text-[15px] text-ink tracking-tight">Tutor</p>
          <p className="font-mono text-[10px] text-mutedink tracking-[0.04em]">
            Ask what to work on, get help, or start something new.
          </p>
        </div>
        {openerData.status === 'ready' && openerData.streak?.current_streak > 0 && (
          <StreakPill streak={openerData.streak} />
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleStartFresh}
            aria-label="Start fresh conversation"
            title="Start fresh"
            className="p-1 text-faint hover:text-read transition-colors"
          >
            <RotateCcw size={16} />
          </button>
          <button
            type="button"
            onClick={closePanel}
            aria-label="Close tutor"
            className="p-1 text-faint hover:text-read transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col">
        <AssistantRuntimeProvider runtime={runtime}>
          {/* Tool UIs registered as children of the provider — assistant-ui's
              makeAssistantToolUI components subscribe to runtime tool calls
              by toolName. Mount once; re-mount has no effect. */}
          <DraftConceptContentUI />
          <Thread
            emptyState={
              booting
                ? <p className="text-sm text-faint px-4 py-3">Opening the chat…</p>
                : <Opener data={openerData} />
            }
            belowMessages={renderBelowMessages({
              handleAddCourse, handleDiscardProposal,
              courseBuilding, courseBuildStage, courseBuildError,
              pendingCurriculumEdit,
              proposal,
              pendingAction,
              handleApplyEdit,
              handleDiscardEdit,
              editApplying,
              editError,
              handleNavigate,
            })}
          />
        </AssistantRuntimeProvider>
      </div>

      {error && (
        <div className="px-4 py-2 border-t border-line">
          <p className="text-xs text-status-bad">{error}</p>
        </div>
      )}
    </aside>
  );
}
